var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { getAllJobs } from '@/api/job.api';
import { usePaginationQuery } from '@/hooks';
import useSessionStorage from '@/hooks/use-session-storage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfigContext } from './config-context';
import { useCustomerDetailContext } from './customer-detail-context';
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
var JobContexts = React.createContext(null);
export var JobsProvider = function (_a) {
    var children = _a.children;
    var _b = useState('recommended'), sortBy = _b[0], setSortBy = _b[1];
    var _c = useSessionStorage({
        key: 'jobFilters',
        initialValue: {
            appliedFilters: [],
        },
        config: { raw: false },
    }), filters = _c[0], setFilters = _c[1], removeFilters = _c[2];
    var _d = useCustomerDetailContext(), customerDetail = _d.customerDetail, recruiterOrganization = _d.recruiterOrganization;
    var _e = useConfigContext().config, jobCard = _e.jobCard, organizationId = _e.organizationId;
    // Memoize the query function to ensure stability
    var getAllJobsQueryFn = useMemo(function () {
        var _a, _b, _c;
        var filtersToApply = (_a = filters === null || filters === void 0 ? void 0 : filters.appliedFilters) === null || _a === void 0 ? void 0 : _a.reduce(function (acc, curr) {
            var _a;
            var key = (_a = curr.queryKey) === null || _a === void 0 ? void 0 : _a.toString();
            if (key) {
                acc[key] = Array.isArray(acc[key])
                    ? __spreadArray(__spreadArray([], acc[key], true), [curr.value], false) : acc[key]
                    ? [acc[key], curr.value]
                    : curr.value;
            }
            return acc;
        }, {});
        var companyIds = new Set(__spreadArray(__spreadArray([], (Array.isArray(filtersToApply['company'])
            ? filtersToApply['company']
            : [filtersToApply['company']]).filter(Boolean), true), [
            (_c = (_b = recruiterOrganization === null || recruiterOrganization === void 0 ? void 0 : recruiterOrganization.organization_id) !== null && _b !== void 0 ? _b : organizationId) !== null && _c !== void 0 ? _c : null,
        ], false).filter(Boolean));
        if (companyIds.size > 0) {
            filtersToApply['company'] = Array.from(companyIds);
        }
        if (filters.search) {
            filtersToApply['search'] = filters.search;
        }
        return function (page) { return getAllJobs(page, filtersToApply, sortBy); };
    }, [filters, sortBy, organizationId, recruiterOrganization === null || recruiterOrganization === void 0 ? void 0 : recruiterOrganization.organization_id]);
    var _f = usePaginationQuery({
        queryFn: getAllJobsQueryFn,
    }), jobs = _f.data, isLoading = _f.isLoading, isLoadingMore = _f.isLoadingMore, fetchNextPage = _f.fetchNextPage, hasMore = _f.hasMore, refetchJobs = _f.refetch, setJobs = _f.setData;
    var updateJob = useCallback(function (jobId, jobPayload) {
        setJobs(jobs.map(function (job) { return (job.jobId === jobId ? __assign(__assign({}, job), jobPayload) : job); }));
    }, [jobs, setJobs]);
    useEffect(function () {
        if (customerDetail) {
            setSortBy('Recommended');
        }
        else {
            setSortBy('Posted Date');
        }
    }, [jobCard === null || jobCard === void 0 ? void 0 : jobCard.allowSorting, customerDetail]);
    return (_jsx(JobContexts.Provider, { value: {
            filters: filters,
            setFilters: setFilters,
            removeFilters: removeFilters,
            jobs: jobs,
            isLoading: isLoading,
            isLoadingMore: isLoadingMore,
            hasMore: hasMore,
            refetchJobs: refetchJobs,
            fetchNextPage: fetchNextPage,
            setSortBy: setSortBy,
            updateJob: updateJob,
        }, children: children }));
};
export var useJobsContext = function () {
    var context = React.useContext(JobContexts);
    if (!context) {
        throw new Error('useJobsContext must be used within a JobsProvider');
    }
    return context;
};
